<template>
  <div class="recurring-sessions-container px-4">
    <v-row class="col-12 mt-2 mb-6 d-flex justify-space-between align-center">
      <h2 class="font-weight-bold">Récurrences : </h2>
      <v-btn
        v-if="currentSession && recurringSessions.length > 4 && !loading"
        small
        outlined
        color="third"
        @click="scrollToCurrentSession"
      >
        <v-icon small class="mr-1">mdi-crosshairs-gps</v-icon>
        Aller à la session actuelle
      </v-btn>
    </v-row>

    <v-row v-if="loading" class="ma-0">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="third" size="48" />
      </v-col>
    </v-row>

    <div v-else-if="recurringSessions.length > 0" class="sessions-content">
      <div class="sessions-list redScroll pa-4">
        <v-row
          v-for="session in recurringSessions"
          :key="session.id"
          class="recurring-session-item align-center pa-1 mb-2"
          :class="{ 'current-session': session.id === currentSession.id }"
        >
          <v-col cols="3">
            <span>{{ formatSessionDate(session.start_time) }}</span>
          </v-col>

          <v-col cols="3" class="d-flex justify-center align-center py-1">
            <span class="mr-2">Heure:</span>
            <v-select
              v-model="session.start_hour"
              :items="computedStartTime"
              label="Debut *"
              class="mr-4"
              item-value="value"
              item-text="label"
              color="third"
              :menu-props="{ bottom: true, offsetY: true }"
            />
            <v-select
              v-model="session.end_hour"
              :items="computedEndTime"
              label="Fin *"
              item-value="value"
              item-text="label"
              color="third"
              :menu-props="{ bottom: true, offsetY: true }"
            />
          </v-col>

          <v-col cols="2" class="d-flex justify-center align-center py-1">
            <v-select
              v-model="session.discipline"
              :items="disciplineOptions"
              label="Discipline"
              item-value="uuid"
              item-text="wtitle"
              return-object
              color="third"
              :menu-props="{ bottom: true, offsetY: true }"
            />
          </v-col>

          <v-col cols="4" class="d-flex justify-center align-center py-1">
            <span class="mr-2">Nom:</span>
            <v-text-field
              v-model="session.name"
              class="mr-2"
              color="third"
            />
          </v-col>
        </v-row>
      </div>

      <v-row class="col-12 d-flex justify-end mt-2">
        <v-btn
          color="third"
          :loading="loading"
          :disabled="!hasUnsavedChanges"
          outlined
          rounded
          class="third--text"

          @click="saveChanges"
        >
          Modifier
          <v-icon size="24" color="third">mdi-plus</v-icon>
        </v-btn>
      </v-row>
    </div>

    <div v-else class="text-center pa-4">
      <v-icon size="48" color="red">mdi-alert-circle</v-icon>
      <p class="red--text">Aucune récurrence trouvée pour cette session.</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject, provide } from '@vue/composition-api';
import { format, fromUnixTime, getUnixTime, set } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sessionsByRecurringUuid } from '../../helpers/amplify';
import { sendNotification } from '@/helpers/utils';
import { updateSessionsBulk } from '@/helpers/sessions';
import store from '@/store';
import { startTimeOptions, endTimeOptions } from '@/helpers/dataManager';

export default {
  name: 'RecurringSessionsList',
  setup () {
    const currentSession = inject('currentSession');
    const loading = ref(false);

    /* Sessions */
    const recurringSessions = ref([]);
    const originalRecurringSessions = ref([]);

    /* Select Options */
    const computedStartTime = computed(() => startTimeOptions().map(time => (time.value)));
    const computedEndTime = computed(() => endTimeOptions().map(time => (time.value)));
    const disciplineOptions = computed(() => store.state.disciplines.map(d => ({ uuid: d.uuid, wtitle: d.name })));

    /* Dates Formats */
    const formatSessionDate = timestamp => ` Le ${format(fromUnixTime(timestamp), 'EEEE dd MMMM yyyy', { locale: fr })} à ${format(fromUnixTime(timestamp), 'HH:mm', { locale: fr })}`;
    const formatToHours = timestamp => format(fromUnixTime(timestamp), 'HH:mm');

    const fetchRecurringSessions = async () => {
      if (!currentSession.value?.recurring_uuid) {
        return;
      }

      try {
        const sessions = await sessionsByRecurringUuid(currentSession.value.recurring_uuid);
        recurringSessions.value = (sessions || []).map(session => ({
          id: session.id,
          name: session.name,
          discipline: session.discipline,
          start_time: session.start_time,
          end_time: session.end_time,
          start_hour: format(fromUnixTime(session.start_time), 'HH:mm'),
          end_hour: format(fromUnixTime(session.end_time), 'HH:mm')
        })).sort((a, b) => a.start_time - b.start_time);

        originalRecurringSessions.value = JSON.parse(JSON.stringify(recurringSessions.value));
      } catch (error) {
        console.log('Error fetching recurring sessions:', JSON.stringify(error, 0, 2));
        sendNotification('error', 'Erreur lors de la récupération des récurrences de session');
        recurringSessions.value = [];
      }
    };

    const getChangedSessions = () => {
      const changedSessions = [];

      recurringSessions.value.forEach(session => {
        const original = originalRecurringSessions.value.find(orig => orig.id === session.id);
        if (!original) return;

        const hasChanges = session.name !== original.name
          || session.discipline?.uuid !== original.discipline?.uuid
          || session.start_hour !== original.start_hour
          || session.end_hour !== original.end_hour;

        if (hasChanges) {
          const updatedSession = { ...session };

          if (session.discipline?.uuid !== original.discipline?.uuid) {
            updatedSession.discipline = session.discipline;
          }

          if (session.name !== original.name) {
            updatedSession.name = session.name;
          }

          if (session.start_hour !== original.start_hour || session.end_hour !== original.end_hour) {
            const sessionDate = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');
            const [startHour, startMinute] = session.start_hour.split(':').map(Number);
            const [endHour, endMinute] = session.end_hour.split(':').map(Number);

            updatedSession.start_time = getUnixTime(set(new Date(sessionDate), { hours: startHour, minutes: startMinute }));
            updatedSession.end_time = getUnixTime(set(new Date(sessionDate), { hours: endHour, minutes: endMinute }));
          }

          const { start_hour, end_hour, ...sessionToUpdate } = updatedSession;
          changedSessions.push(sessionToUpdate);
        }
      });

      return changedSessions;
    };

    const updateWithChangedSessions = (sessionsArray, changedSessions) => sessionsArray.map(session => {
      const changedSession = changedSessions.find(c => c.id === session.id);
      if (changedSession) {
        return { ...session, ...changedSession };
      }
      return session;
    });

    const saveChanges = async () => {
      const changedSessions = getChangedSessions();

      if (!changedSessions.length) {
        sendNotification('error', 'Aucune modification détectée');
        return;
      }

      loading.value = true;

      try {
        const success = await updateSessionsBulk(changedSessions, ['name', 'discipline', 'start_time', 'end_time']);

        if (success) {
          recurringSessions.value = updateWithChangedSessions(recurringSessions.value, changedSessions);
          originalRecurringSessions.value = updateWithChangedSessions(originalRecurringSessions.value, changedSessions);
          sendNotification('success', 'Sessions mises à jour avec succès');
        }
      } catch (error) {
        sendNotification('error', 'Erreur lors de la mise à jour des sessions');
      } finally {
        loading.value = false;
      }
    };

    const hasUnsavedChanges = computed(() => getChangedSessions().length > 0);

    const scrollToCurrentSession = () => {
      const currentSessionElement = document.querySelector('.recurring-session-item.current-session');
      if (currentSessionElement) {
        currentSessionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    };

    onMounted(async () => {
      loading.value = true;
      await fetchRecurringSessions();
      loading.value = false;
    });

    provide('newSession', currentSession);

    return {
      recurringSessions,
      loading,
      currentSession,
      formatSessionDate,
      formatToHours,
      disciplineOptions,
      computedStartTime,
      computedEndTime,
      saveChanges,
      hasUnsavedChanges,
      scrollToCurrentSession
    };
  }
};
</script>

<style scoped>
.recurring-sessions-container {
  display: flex;
  flex-direction: column;
  max-height: 600px;
}

.sessions-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.sessions-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.recurring-session-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
  transition: background-color 0.2s;
}

.recurring-session-item:hover {
  background-color: #f5f5f5;
}

.recurring-session-item.current-session {
  background-color: #e84f4f0f;
  border-color: #aa0f0f;
}
</style>
